// LuckySpin.js — Daily lucky spin wheel for NeevStar
import React, { useState, useEffect, useRef } from 'react';
import { showShareCard } from './ShareCard';

const css = `
@keyframes ls-popIn {
  0%   { transform: scale(0.3) rotate(-10deg); opacity: 0; }
  65%  { transform: scale(1.06) rotate(2deg); }
  100% { transform: scale(1) rotate(0); opacity: 1; }
}
@keyframes ls-resultPop {
  0%   { transform: scale(0.2); opacity: 0; }
  60%  { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}
@keyframes ls-arrowBounce {
  0%,100% { transform: translateX(-50%) translateY(0); }
  50%      { transform: translateX(-50%) translateY(-6px); }
}
@keyframes ls-glow {
  0%,100% { box-shadow: 0 0 20px rgba(255,214,0,0.3); }
  50%      { box-shadow: 0 0 50px rgba(255,214,0,0.8); }
}
@keyframes ls-btnPulse {
  0%,100% { transform: scale(1); box-shadow: 0 4px 20px rgba(255,214,0,0.4); }
  50%      { transform: scale(1.07); box-shadow: 0 6px 28px rgba(255,214,0,0.7); }
}
@keyframes ls-confetti {
  0%   { transform: translateY(0) rotate(0); opacity: 1; }
  100% { transform: translateY(80px) rotate(360deg); opacity: 0; }
}
.ls-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.9);
  z-index: 9994;
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
  font-family: 'Nunito', sans-serif;
}
.ls-card {
  background: linear-gradient(160deg, #0D1B4B 0%, #1A0533 60%, #0A0A2E 100%);
  border: 2px solid rgba(255,214,0,0.5);
  border-radius: 28px;
  padding: 24px 20px 20px;
  max-width: 380px;
  width: 100%;
  text-align: center;
  animation: ls-popIn 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards;
  position: relative;
  overflow: hidden;
}
.ls-wheel-wrap {
  position: relative;
  width: 280px;
  height: 280px;
  margin: 0 auto 12px;
}
.ls-wheel {
  width: 100%; height: 100%;
  border-radius: 50%;
  position: relative;
  transition: transform 4s cubic-bezier(0.17,0.67,0.12,1);
  filter: drop-shadow(0 0 16px rgba(255,214,0,0.4));
}
.ls-pointer {
  position: absolute;
  top: -18px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 28px;
  z-index: 10;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.6));
  animation: ls-arrowBounce 1.2s ease-in-out infinite;
}
.ls-pointer.spinning { animation: none; }
.ls-center-circle {
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%,-50%);
  width: 50px; height: 50px;
  background: linear-gradient(135deg, #FFD600, #FF9500);
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 22px;
  z-index: 5;
  box-shadow: 0 0 16px rgba(255,214,0,0.6);
  border: 3px solid rgba(255,255,255,0.3);
}
.ls-spin-btn {
  background: linear-gradient(135deg, #FFD600, #FF9500);
  border: none; border-radius: 16px;
  padding: 14px 36px;
  font-size: 17px; font-weight: 900;
  color: #1A1A2E;
  cursor: pointer;
  font-family: 'Nunito', sans-serif;
  animation: ls-btnPulse 1.8s ease-in-out infinite;
  box-shadow: 0 4px 20px rgba(255,150,0,0.4);
}
.ls-spin-btn:disabled {
  animation: none;
  background: #555;
  color: #888;
  cursor: default;
  box-shadow: none;
}
.ls-result-card {
  position: absolute;
  inset: 0;
  background: rgba(13,27,75,0.96);
  border-radius: 28px;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 24px;
  animation: ls-resultPop 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards;
}
.ls-confetti-piece {
  position: absolute;
  width: 8px; height: 8px;
  border-radius: 2px;
  animation: ls-confetti 1s ease-in forwards;
}
`;

// Segments: label, color, weight (higher = more common), type, value
const SEGMENTS = [
  { label: '+20 Coins',       emoji: '🪙',  color: '#1A6FD4', darkColor: '#1458A8', weight: 25, type: 'coins',   value: 20  },
  { label: '+50 Coins',       emoji: '🪙',  color: '#8B2BE2', darkColor: '#6B1FAF', weight: 20, type: 'coins',   value: 50  },
  { label: '+100 Coins',      emoji: '💰',  color: '#FF6B00', darkColor: '#CC5500', weight: 18, type: 'coins',   value: 100 },
  { label: 'Mystery Badge',   emoji: '🏅',  color: '#00A86B', darkColor: '#007A4D', weight: 15, type: 'badge',   value: 0   },
  { label: 'Double XP 1hr',   emoji: '⚡',  color: '#FF2D55', darkColor: '#CC1A3F', weight: 10, type: 'doublexp',value: 0   },
  { label: 'Streak Shield',   emoji: '🛡️', color: '#0066FF', darkColor: '#004DCC', weight: 7,  type: 'shield',  value: 0   },
  { label: '+100 Coins',      emoji: '💎',  color: '#FFD600', darkColor: '#CCA800', weight: 4,  type: 'coins',   value: 100 },
  { label: 'Legendary Chest', emoji: '👑',  color: '#FF2D55', darkColor: '#CC1A3F', weight: 1,  type: 'chest',   value: 300 },
];

const CONFETTI_COLORS = ['#FFD600','#FF2D55','#00C851','#0066FF','#8B2BE2','#FF9500'];
const SEG_COUNT = SEGMENTS.length;
const SEG_ANGLE = 360 / SEG_COUNT; // 45°

function weightedRandom() {
  const total = SEGMENTS.reduce((s, seg) => s + seg.weight, 0);
  let rand = Math.random() * total;
  for (let i = 0; i < SEGMENTS.length; i++) {
    rand -= SEGMENTS[i].weight;
    if (rand <= 0) return i;
  }
  return 0;
}

function canSpinToday(userEmail) {
  const key = `ns_spin_${userEmail}_${new Date().toDateString()}`;
  return !localStorage.getItem(key);
}

function markSpinUsed(userEmail) {
  const key = `ns_spin_${userEmail}_${new Date().toDateString()}`;
  localStorage.setItem(key, '1');
}

export default function LuckySpin({ onClose, onCoinsEarned, onXPEarned, profile }) {
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null);
  const [confetti, setConfetti] = useState([]);
  const extraSpinKey = `ns_extra_spins_${profile?.parent_email || 'guest'}`;
  const extraSpins = parseInt(localStorage.getItem(extraSpinKey) || '0', 10);
  const [usedExtraSpin, setUsedExtraSpin] = useState(false);
  const [alreadySpun, setAlreadySpun] = useState(!canSpinToday(profile?.parent_email || 'guest'));
  const wheelRef = useRef(null);

  useEffect(() => {
    if (!document.getElementById('ls-css')) {
      const s = document.createElement('style');
      s.id = 'ls-css';
      s.textContent = css;
      document.head.appendChild(s);
    }
  }, []);

  const doSpin = () => {
    if (spinning || alreadySpun) return;
    setSpinning(true);

    const winIndex = weightedRandom();
    // Calculate target rotation:
    // Wheel starts at 0. Segment i is at i * 45 degrees.
    // Pointer is at top (0°). We want segment winIndex to land under pointer.
    // Center of winIndex segment = winIndex * SEG_ANGLE + SEG_ANGLE/2
    // We need to rotate so that center lands at 0° (top)
    // Extra full spins for drama: 5–8 full rotations
    const extraSpins = (5 + Math.floor(Math.random() * 4)) * 360;
    const segCenter = winIndex * SEG_ANGLE + SEG_ANGLE / 2;
    const targetRot = rotation + extraSpins + (360 - segCenter);

    setRotation(targetRot);

    // After animation ends (4s transition)
    setTimeout(() => {
      const won = SEGMENTS[winIndex];
      setResult(won);
      setSpinning(false);
      markSpinUsed(profile?.parent_email || 'guest');
      // Confetti burst
      setConfetti(Array.from({ length: 24 }, (_, i) => ({
        id: i,
        left: `${Math.random() * 100}%`,
        top: `${10 + Math.random() * 50}%`,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        delay: `${Math.random() * 0.5}s`,
        size: 6 + Math.random() * 8,
      })));
      // Award
      if (won.type === 'coins' || won.type === 'chest') onCoinsEarned && onCoinsEarned(won.value);
      // Show share card on legendary chest win
      if (won.type === 'chest') {
        setTimeout(() => showShareCard('legendary', 'Legendary Chest from Lucky Spin!', profile?.child_name, won.value), 500);
      }
      if (won.type === 'doublexp') {/* Could set a localStorage flag for double XP */}
    }, 4200);
  };

  const handleClaim = () => {
    onClose();
  };

  // Build wheel as SVG
  const R = 140; // radius
  const cx = 140, cy = 140;
  const paths = SEGMENTS.map((seg, i) => {
    const startAngle = (i * SEG_ANGLE - 90) * (Math.PI / 180);
    const endAngle = ((i + 1) * SEG_ANGLE - 90) * (Math.PI / 180);
    const x1 = cx + R * Math.cos(startAngle);
    const y1 = cy + R * Math.sin(startAngle);
    const x2 = cx + R * Math.cos(endAngle);
    const y2 = cy + R * Math.sin(endAngle);
    const midAngle = (startAngle + endAngle) / 2;
    const tx = cx + (R * 0.68) * Math.cos(midAngle);
    const ty = cy + (R * 0.68) * Math.sin(midAngle);
    const textAngle = ((i * SEG_ANGLE + SEG_ANGLE / 2) - 90);
    return { seg, x1, y1, x2, y2, tx, ty, textAngle, startAngle, endAngle };
  });

  return (
    <div className="ls-overlay" onClick={e => e.target.classList.contains('ls-overlay') && !spinning && handleClaim()}>
      <div className="ls-card">

        {/* Confetti */}
        {confetti.map(c => (
          <div key={c.id} className="ls-confetti-piece"
            style={{ left: c.left, top: c.top, background: c.color, width: c.size, height: c.size, animationDelay: c.delay }} />
        ))}

        {/* Header */}
        <p style={{ fontSize: 11, fontWeight: 900, color: 'rgba(255,214,0,0.7)', letterSpacing: '0.1em', margin: '0 0 4px' }}>
          DAILY LUCKY SPIN
        </p>
        <h2 style={{ fontSize: 20, fontWeight: 900, color: 'white', margin: '0 0 16px' }}>
          🎰 Spin to Win!
        </h2>

        {/* Wheel */}
        <div className="ls-wheel-wrap">
          <div className={`ls-pointer ${spinning ? 'spinning' : ''}`}>▼</div>

          <div
            ref={wheelRef}
            className="ls-wheel"
            style={{ transform: `rotate(${rotation}deg)` }}
          >
            <svg width="280" height="280" viewBox="0 0 280 280">
              {/* Outer ring */}
              <circle cx="140" cy="140" r="138" fill="none" stroke="rgba(255,214,0,0.5)" strokeWidth="3" />

              {paths.map(({ seg, x1, y1, x2, y2, tx, ty, textAngle }, i) => (
                <g key={i}>
                  {/* Segment fill */}
                  <path
                    d={`M ${cx} ${cy} L ${x1} ${y1} A ${R} ${R} 0 0 1 ${x2} ${y2} Z`}
                    fill={i % 2 === 0 ? seg.color : seg.darkColor}
                    stroke="rgba(255,255,255,0.15)"
                    strokeWidth="1.5"
                  />
                  {/* Emoji */}
                  <text
                    x={tx}
                    y={ty}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="18"
                    transform={`rotate(${textAngle}, ${tx}, ${ty})`}
                  >
                    {seg.emoji}
                  </text>
                </g>
              ))}

              {/* Inner gold ring */}
              <circle cx="140" cy="140" r="30" fill="#1A1A2E" stroke="rgba(255,214,0,0.4)" strokeWidth="2" />
            </svg>
          </div>

          {/* Center */}
          <div className="ls-center-circle">⭐</div>
        </div>

        {/* Segments legend */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center', marginBottom: 16 }}>
          {SEGMENTS.map((seg, i) => (
            <span key={i} style={{
              background: `${seg.color}33`,
              border: `1px solid ${seg.color}66`,
              borderRadius: 8, padding: '3px 8px',
              fontSize: 10, fontWeight: 800, color: 'rgba(255,255,255,0.8)',
            }}>
              {seg.emoji} {seg.label}
            </span>
          ))}
        </div>

        {/* Spin button */}
        {!result && (
          <>
            {alreadySpun ? (
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: 28, margin: '0 0 6px' }}>⏳</p>
                <p style={{ fontSize: 14, fontWeight: 900, color: 'rgba(255,255,255,0.6)', margin: '0 0 4px' }}>
                  Already spun today!
                </p>
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', fontWeight: 700, margin: '0 0 14px' }}>
                  Come back tomorrow for your next spin 🌟
                </p>
                {extraSpins > 0 && !usedExtraSpin && (
                  <button onClick={() => {
                    // Use one extra spin
                    const remaining = extraSpins - 1;
                    localStorage.setItem(extraSpinKey, String(remaining));
                    setUsedExtraSpin(true);
                    setAlreadySpun(false);
                  }} style={{
                    background: 'linear-gradient(135deg,#FFD600,#FF9500)',
                    border: 'none', borderRadius: 14, padding: '12px 28px',
                    color: '#1A1A2E', fontSize: 14, fontWeight: 900,
                    cursor: 'pointer', fontFamily: "'Nunito',sans-serif",
                    marginBottom: 10, display: 'block', width: '100%',
                  }}>
                    🎰 Use Extra Spin ({extraSpins} left)
                  </button>
                )}
                <button onClick={handleClaim} style={{
                  background: 'rgba(255,255,255,0.1)', border: '1.5px solid rgba(255,255,255,0.2)',
                  borderRadius: 12, padding: '10px 28px', color: 'white',
                  fontSize: 14, fontWeight: 800, cursor: 'pointer', fontFamily: "'Nunito',sans-serif",
                }}>
                  Close
                </button>
              </div>
            ) : (
              <button
                className="ls-spin-btn"
                onClick={doSpin}
                disabled={spinning}
              >
                {spinning ? '🌀 Spinning...' : '🎰 SPIN!'}
              </button>
            )}
          </>
        )}

        {/* Result overlay */}
        {result && (
          <div className="ls-result-card">
            <p style={{ fontSize: 11, fontWeight: 900, color: 'rgba(255,214,0,0.7)', letterSpacing: '0.1em', margin: '0 0 8px' }}>
              YOU WON!
            </p>
            <div style={{ fontSize: 64, margin: '0 0 8px' }}>{result.emoji}</div>
            <h2 style={{ fontSize: 24, fontWeight: 900, color: 'white', margin: '0 0 4px' }}>
              {result.label}
            </h2>
            {result.type === 'coins' || result.type === 'chest' ? (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: 'rgba(255,214,0,0.15)',
                border: '1.5px solid rgba(255,214,0,0.4)',
                borderRadius: 14, padding: '8px 20px', margin: '8px 0 16px',
              }}>
                <span style={{ fontSize: 22 }}>🪙</span>
                <span style={{ fontSize: 20, fontWeight: 900, color: '#FFD600' }}>+{result.value} NeevCoins added!</span>
              </div>
            ) : (
              <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', fontWeight: 700, margin: '8px 0 16px' }}>
                {result.type === 'doublexp' ? 'Double XP active for 1 hour! ⚡' :
                 result.type === 'badge' ? 'Mystery Badge added to your profile! 🏅' :
                 result.type === 'shield' ? 'Streak Shield activated! 🛡️' : ''}
              </p>
            )}
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', fontWeight: 700, margin: '0 0 16px' }}>
              Come back tomorrow for another spin!
            </p>
            <button onClick={handleClaim} style={{
              background: 'linear-gradient(135deg,#FFD600,#FF9500)',
              border: 'none', borderRadius: 14, padding: '13px 32px',
              fontSize: 16, fontWeight: 900, color: '#1A1A2E',
              cursor: 'pointer', fontFamily: "'Nunito',sans-serif",
              boxShadow: '0 6px 20px rgba(255,150,0,0.4)',
            }}>
              ✅ Awesome!
            </button>
          </div>
        )}

        {/* Close */}
        {!result && !spinning && (
          <button onClick={handleClaim} style={{
            background: 'none', border: 'none',
            color: 'rgba(255,255,255,0.3)', fontSize: 12, fontWeight: 700,
            cursor: 'pointer', marginTop: 10, fontFamily: "'Nunito',sans-serif",
          }}>
            close
          </button>
        )}
      </div>
    </div>
  );
}

export { canSpinToday };
